import * as React from "react"
import { toast as sonnerToast } from "sonner"

// Type definitions for backward compatibility with old toast API
type ToastVariant = "default" | "destructive"

interface ToastOptions {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  duration?: number
  action?: React.ReactNode
}

/**
 * Compatibility wrapper around Sonner's toast API
 * Maintains the same interface as the old shadcn toast system
 * so all existing toast calls continue working without changes
 */
function toast({ title, description, variant, duration, action }: ToastOptions) {
  const message = title || description || ""
  const hasDescription = title && description

  // Map variant to Sonner's toast types
  if (variant === "destructive") {
    if (hasDescription) {
      return sonnerToast.error(title, {
        description: description as string,
        duration: duration || 4000,
        action: action as any,
      })
    }
    return sonnerToast.error(message as string, {
      duration: duration || 4000,
      action: action as any,
    })
  }

  // Default toast
  if (hasDescription) {
    return sonnerToast(title, {
      description: description as string,
      duration: duration || 4000,
      action: action as any,
    })
  }

  return sonnerToast(message as string, {
    duration: duration || 4000,
    action: action as any,
  })
}

/**
 * Hook that returns the toast function
 * Maintains backward compatibility with old useToast hook
 * Returns empty toasts array for compatibility with old Toaster component
 */
function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
    toasts: [], // Empty array for backward compatibility with old Toaster
  }
}

export { useToast, toast }
